import {
  InvoiceIncoming,
  InvoiceIncomingCostAllocation,
  InvoiceIncomingEditFormValues,
  InvoiceIncomingItemEditFormValues,
  InvoiceIncomingRow,
  InvoiceIncomingDocumentQueue,
  InvoiceIncomingExtraction,
} from "./types";
import { InvoiceOutgoingItemEditFormValues } from "services/api/fleet/vehicles/invoices/types";
import { transformInvoiceStatus } from "services/api/fleet/vehicles/invoices/transformations";

export const transformInvoiceIncomingRow = (
  res: any
): InvoiceIncomingRow => {
  return {
    id: res.id,
    invoiceNumber: res.invoiceNumber,
    contactSupplier: res.contactSupplier ?? null,
    supplierName: res.supplierName ?? null,
    vehicle: res.vehicle ?? null,
    invoiceDate: res.invoiceDate,
    dueDate: res.dueDate,
    totalWithoutTax: res.totalWithoutTax ?? null,
    totalTax: res.totalTax ?? null,
    total: res.total,
    status: res.status ? transformInvoiceStatus(res.status) : null,
    incomingSourceId: res.incomingSourceId ?? null,
    isPaid: res.isPaid ?? false,
    documents: res.documents ?? [],
    childRows: (res.items ?? []).filter((item: any) => !item.deleted),
    totalPaid: res.totalPaid ?? 0,
    remainingAmount: res.remainingAmount ?? 0,
    allocationStatus: res.allocationStatus ?? null,
    allocatedItemCount: res.allocatedItemCount ?? 0,
    itemCount: res.itemCount ?? 0,
  };
};

export const transformInvoiceIncoming = (
  res: any
): InvoiceIncoming => ({
  id: res.id,
  invoiceNumber: res.invoiceNumber,
  contactSupplier: res.contactSupplier ?? null,
  supplierName: res.supplierName ?? null,
  supplierAddress: res.supplierAddress ?? null,
  supplierOIB: res.supplierOIB ?? null,
  supplierVAT: res.supplierVAT ?? null,
  supplierIBAN: res.supplierIBAN ?? null,
  vehicle: res.vehicle ?? null,
  invoiceDate: res.invoiceDate,
  dueDate: res.dueDate,
  durationDays: res.durationDays ?? null,
  deliveryDate: res.deliveryDate ?? null,
  totalWithoutTax: res.totalWithoutTax ?? null,
  totalTax: res.totalTax ?? null,
  total: res.total ?? null,
  currencyId: res.currencyId ?? null,
  exchangeRate: res.exchangeRate ?? null,
  status: res.status ? transformInvoiceStatus(res.status) : null,
  incomingSourceId: res.incomingSourceId ?? null,
  providerName: res.providerName ?? null,
  providerInvoiceId: res.providerInvoiceId ?? null,
  receivedFromProviderAt: res.receivedFromProviderAt ?? null,
  created: res.created ?? null,
  updated: res.updated ?? null,
  processStatusReportedAt: res.processStatusReportedAt ?? null,
  rejectionReasonType: res.rejectionReasonType ?? null,
  rejectionReason: res.rejectionReason ?? null,
  rejectionFiscalizedAt: res.rejectionFiscalizedAt ?? null,
  note: res.note ?? null,
  internalNote: res.internalNote ?? null,
  expenseTypeId: res.expenseTypeId ?? null,
  isPaid: res.isPaid ?? false,
  paidAt: res.paidAt ?? null,
  paymentMethodId: res.paymentMethodId ?? null,
  paymentReference: res.paymentReference ?? null,
  items: res.items ?? [],
  documents: res.documents ?? [],
  payments: res.payments ?? [],
  totalPaid: res.totalPaid ?? 0,
  remainingAmount: res.remainingAmount ?? 0,
});

export const transformInvoiceIncomingEditForm = (
  invoice: InvoiceIncoming
): InvoiceIncomingEditFormValues => ({
  id: invoice.id,
  vehicleId: invoice.vehicle?.id ?? null,
  contactSupplierId: invoice.contactSupplier?.id ?? null,
  supplierName: invoice.supplierName ?? null,
  supplierAddress: invoice.supplierAddress ?? null,
  supplierOIB: invoice.supplierOIB ?? null,
  supplierVAT: invoice.supplierVAT ?? null,
  supplierIBAN: invoice.supplierIBAN ?? null,
  invoiceNumber: invoice.invoiceNumber ?? null,
  invoiceDate: invoice.invoiceDate,
  dueDate: invoice.dueDate,
  durationDays: invoice.durationDays,
  deliveryDate: invoice.deliveryDate,
  totalWithoutTax: invoice.totalWithoutTax,
  totalTax: invoice.totalTax,
  total: invoice.total,
  currencyId: invoice.currencyId ?? null,
  exchangeRate: invoice.exchangeRate ?? null,
  statusId: invoice.status?.id ?? null,
  incomingSourceId: invoice.incomingSourceId ?? null,
  note: invoice.note ?? null,
  internalNote: invoice.internalNote ?? null,
  expenseTypeId: invoice.expenseTypeId ?? null,
  paymentMethodId: invoice.paymentMethodId ?? null,
  paymentReference: invoice.paymentReference ?? null,
  // Vrsta stavke se ne sprema na ulaznom računu — obrazac je pamti samo dok se stavka uređuje.
  items: (invoice.items ?? []).map(item => ({ ...item, expenseTypeId: null, billingItemTypeId: null })),
});

/**
 * Stavka ulaznog računa → obrazac za unos stavke. Obrazac je zajednički s izlaznim računima
 * (`InvoiceItemForm`), pa se stavka prevodi u njegov oblik i natrag.
 */
export const transformInvoiceIncomingItemToForm = (
  item: InvoiceIncomingItemEditFormValues
): InvoiceOutgoingItemEditFormValues => ({
  id: item.id,
  code: item.code,
  name: item.name,
  taxRate: item.taxRate,
  quantity: item.quantity,
  priceWithoutTax: item.priceWithoutTax,
  priceWithTax: item.priceWithTax,
  discountAmount: item.discountAmount,
  discountTypeId: item.discountTypeId,
  discountPercent: item.discountPercent,
  total: item.total,
  note: item.note,
  kpdCode: item.kpdCode,
  unitOfMeasureId: item.unitOfMeasureId,
  billingItemTypeId: item.billingItemTypeId,
  billingItemTypeName: item.billingItemTypeName ?? null,
});

export const transformInvoiceIncomingItemFromForm = (
  form: InvoiceOutgoingItemEditFormValues,
  expenseTypeId: string | null = null
): InvoiceIncomingItemEditFormValues => ({
  id: form.id ?? crypto.randomUUID(),
  code: form.code,
  name: form.name,
  quantity: form.quantity,
  priceWithoutTax: form.priceWithoutTax,
  priceWithTax: form.priceWithTax,
  taxRate: form.taxRate,
  discountTypeId: form.discountTypeId,
  discountAmount: form.discountAmount,
  discountPercent: form.discountPercent,
  total: form.total,
  note: form.note,
  kpdCode: form.kpdCode,
  unitOfMeasureId: form.unitOfMeasureId,
  expenseTypeId,
  billingItemTypeId: form.billingItemTypeId,
  billingItemTypeName: form.billingItemTypeName ?? null,
});

export const transformInvoiceIncomingOnCreate = (
  formValues: InvoiceIncomingEditFormValues
): any => ({
  // Backend očekuje `fleetVehicleId` i `invoiceIncomingStatusId`.
  fleetVehicleId: formValues.vehicleId,
  contactSupplierId: formValues.contactSupplierId,
  supplierName: formValues.supplierName,
  supplierAddress: formValues.supplierAddress,
  supplierOIB: formValues.supplierOIB,
  supplierVAT: formValues.supplierVAT,
  supplierIBAN: formValues.supplierIBAN,
  invoiceNumber: formValues.invoiceNumber,
  invoiceDate: formValues.invoiceDate,
  dueDate: formValues.dueDate,
  durationDays: formValues.durationDays,
  deliveryDate: formValues.deliveryDate,
  totalWithoutTax: formValues.totalWithoutTax,
  totalTax: formValues.totalTax,
  total: formValues.total,
  currencyId: formValues.currencyId,
  exchangeRate: formValues.exchangeRate,
  invoiceIncomingStatusId: formValues.statusId,
  incomingSourceId: formValues.incomingSourceId,
  note: formValues.note,
  internalNote: formValues.internalNote,
  expenseTypeId: formValues.expenseTypeId,
  paymentMethodId: formValues.paymentMethodId,
  paymentReference: formValues.paymentReference,
  items: formValues.items,
  // Datoteke iz kojih je račun pročitan ostaju priložene računu.
  uploadIds: formValues.uploadIds ?? [],
});

export const transformInvoiceIncomingOnUpdate = (
  formValues: InvoiceIncomingEditFormValues
): any => ({
  id: formValues.id,
  fleetVehicleId: formValues.vehicleId,
  contactSupplierId: formValues.contactSupplierId,
  supplierName: formValues.supplierName,
  supplierAddress: formValues.supplierAddress,
  supplierOIB: formValues.supplierOIB,
  supplierVAT: formValues.supplierVAT,
  supplierIBAN: formValues.supplierIBAN,
  invoiceNumber: formValues.invoiceNumber,
  invoiceDate: formValues.invoiceDate,
  dueDate: formValues.dueDate,
  durationDays: formValues.durationDays,
  deliveryDate: formValues.deliveryDate,
  totalWithoutTax: formValues.totalWithoutTax,
  totalTax: formValues.totalTax,
  total: formValues.total,
  currencyId: formValues.currencyId,
  exchangeRate: formValues.exchangeRate,
  invoiceIncomingStatusId: formValues.statusId,
  incomingSourceId: formValues.incomingSourceId,
  note: formValues.note,
  internalNote: formValues.internalNote,
  expenseTypeId: formValues.expenseTypeId,
  paymentMethodId: formValues.paymentMethodId,
  paymentReference: formValues.paymentReference,
  items: formValues.items,
});

/** Redak raspodjele iz API-ja — brojevi dolaze kao decimal, id troška može biti null. */
export const transformInvoiceIncomingCostAllocation = (
  res: any
): InvoiceIncomingCostAllocation => ({
  expenseId: res.expenseId ?? null,
  invoiceIncomingItemId: res.invoiceIncomingItemId,
  targetTypeId: res.targetTypeId ?? "vehicle",
  vehicleId: res.vehicleId ?? null,
  vehicleName: res.vehicleName ?? null,
  expenseTypeId: res.expenseTypeId ?? null,
  expenseTypeName: res.expenseTypeName ?? null,
  officeId: res.officeId ?? null,
  quantity: res.quantity ?? null,
  amount: res.amount ?? null,
});

export const transformInvoiceIncomingDocumentQueue = (
  res: any
): InvoiceIncomingDocumentQueue => ({
  queue: (res?.queue ?? []).map((item: any) => ({
    uploadId: item.uploadId,
    fileName: item.fileName,
    fileExtension: item.fileExtension,
    url: item.url ?? "",
    extraction: null,
  })),
  rejected: (res?.rejected ?? []).map((item: any) => ({
    fileName: item.fileName,
    reason: item.reason,
  })),
});

export const transformInvoiceIncomingExtraction = (
  res: any
): InvoiceIncomingExtraction => ({
  uploadId: res.uploadId,
  supplierName: res.supplierName ?? null,
  supplierOIB: res.supplierOIB ?? null,
  supplierAddress: res.supplierAddress ?? null,
  supplierIBAN: res.supplierIBAN ?? null,
  contactSupplierId: res.contactSupplierId ?? null,
  invoiceNumber: res.invoiceNumber ?? null,
  invoiceDate: res.invoiceDate ? new Date(res.invoiceDate) : null,
  dueDate: res.dueDate ? new Date(res.dueDate) : null,
  currencyId: res.currencyId ?? null,
  totalWithoutTax: res.totalWithoutTax ?? null,
  totalTax: res.totalTax ?? null,
  total: res.total ?? null,
  expenseTypeId: res.expenseTypeId ?? null,
  confidence: res.confidence ?? null,
  warnings: res.warnings ?? [],
  fieldLocations: res.fieldLocations ?? {},
  fieldConfidences: res.fieldConfidences ?? {},
  detectedRotation: res.detectedRotation ?? 0,
  items: (res.items ?? []).map((item: any) => ({
    code: item.code ?? null,
    name: item.name ?? null,
    quantity: item.quantity ?? null,
    priceWithoutTax: item.priceWithoutTax ?? null,
    taxRate: item.taxRate ?? null,
    discountPercent: item.discountPercent ?? null,
    total: item.total ?? null,
  })),
});
